/**
 * Shared presentational primitives for the clinical-admin dashboard.
 * Kept local to the feature so it can iterate without touching the
 * other admin screens (which each define their own table/badge helpers).
 */
import { useEffect } from 'react';
import type { ReactNode } from 'react';

export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function fmtMoney(
  cents: number | null | undefined,
  currency: string | null | undefined = 'usd',
): string {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency ?? 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

// Action-oriented labels for patients.onboarding_state — "signed_up"
// reads as "Awaiting intake" so the table flags what's actually needed.
const _ONBOARDING_LABELS: Record<string, string> = {
  signed_up: 'Awaiting intake',
  intake_in_progress: 'Intake in progress',
  intake_submitted: 'Intake complete',
  scheduling_required: 'Scheduling required',
  complete: 'Onboarded',
};

export function onboardingLabel(value: string | null | undefined): string {
  if (!value) return '—';
  return _ONBOARDING_LABELS[value] ?? value.replace(/_/g, ' ');
}

/** Internal test accounts use @systemlabs.com addresses. */
export function isTestEmail(email: string | null | undefined): boolean {
  return (email ?? '').trim().toLowerCase().endsWith('@systemlabs.com');
}

export function Card({
  title,
  action,
  children,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="border border-line rounded-lg bg-raised overflow-hidden">
      {(title || action) && (
        <div className="flex items-center justify-between gap-12 px-15 py-12 border-b border-line">
          <span className="type-label-md font-semibold">{title}</span>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Th({ children }: { children: ReactNode }) {
  return (
    <th className="text-left type-label-sm font-medium text-ink-muted px-12 py-8 whitespace-nowrap">
      {children}
    </th>
  );
}

export function Td({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <td className={`px-12 py-8 align-top ${className}`}>{children}</td>
  );
}

type Tone = 'neutral' | 'good' | 'warn' | 'danger' | 'accent';

const TONE_CLASS: Record<Tone, string> = {
  neutral: 'bg-sunken text-ink-muted',
  good: 'bg-sunken text-accent',
  warn: 'bg-danger-wash text-danger',
  danger: 'bg-danger-wash text-danger',
  accent: 'bg-sunken text-accent',
};

export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: Tone;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-flex items-center gap-4 type-10 uppercase tracking-wide rounded px-6 py-2 ${TONE_CLASS[tone]}`}
    >
      {children}
    </span>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="px-15 py-30 text-center type-label-sm text-ink-muted">
      {children}
    </div>
  );
}

export function ErrorState({ children }: { children: ReactNode }) {
  return (
    <div className="px-15 py-30 text-center type-label-sm text-danger">
      {children}
    </div>
  );
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="px-15 py-30 text-center type-label-sm text-ink-muted">
      {label}
    </div>
  );
}

/**
 * Lightweight self-dismissing toast — fixed bottom-right. Self-contained
 * (no global provider): a screen holds the message in state and clears it
 * via `onDone`.
 */
export function Toast({
  message,
  tone = 'good',
  onDone,
}: {
  message: string;
  tone?: 'good' | 'danger';
  onDone: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDone, 3500);
    return () => clearTimeout(t);
  }, [message, onDone]);
  return (
    <div
      className={`fixed bottom-24 right-24 z-[100] type-label-sm px-14 py-10 rounded-lg shadow-lg border ${
        tone === 'danger'
          ? 'bg-danger-wash text-danger border-line'
          : 'bg-ink text-page border-ink'
      }`}
    >
      {message}
    </div>
  );
}
